import React, { useCallback, useEffect, useState, VFC } from 'react';
import uniq from 'lodash/uniq';

import { ToggleComponent } from 'src/common/form/toggle/component';
import { MultiSelectInput } from 'src/common/multi-select/input';
import { ShareLinkFieldType } from '../link-config-form.factory';
import { TooltipComponent } from 'src/common/tooltip/component';

interface ShareWhitelistBlacklistProps {
  whitelistedEmails: ShareLinkFieldType<string[]>;
  blacklistedEmails: ShareLinkFieldType<string[]>;
  onFieldChange: <T>(payload: ShareLinkFieldType<T>) => void;
  disabled?: boolean;
}

interface ShareEmailCaptureProps extends ShareWhitelistBlacklistProps {
  emailRequired: ShareLinkFieldType<boolean>;
  emailVerificationRequired: ShareLinkFieldType<boolean>;
  ndaRequired: ShareLinkFieldType<boolean>;
  disabled?: boolean;
}

const DOMAIN_REGEX = /^[a-zA-Z0-9][a-zA-Z0-9-]{0,61}[a-zA-Z0-9](?:\.[a-zA-Z]{2,})+$/;
export const EMAIL_GLOBAL_REGEX =
  // eslint-disable-next-line no-control-regex
  /(?:[a-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-z0-9!#$%&'*+/=?^_`{|}~-]+)*|"(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21\x23-\x5b\x5d-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])*")@(?:(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z0-9](?:[a-z0-9-]*[a-z0-9])?|\[(?:(?:(2(5[0-5]|[0-4][0-9])|1[0-9][0-9]|[1-9]?[0-9]))\.){3}(?:(2(5[0-5]|[0-4][0-9])|1[0-9][0-9]|[1-9]?[0-9])|[a-z0-9-]*[a-z0-9]:(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21-\x5a\x53-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])+)\])/g;

const ShareWhitelistBlacklist = ({
  whitelistedEmails,
  blacklistedEmails,
  onFieldChange,
  disabled = false,
}: ShareWhitelistBlacklistProps) => {
  const onItemAded = (stack: ShareLinkFieldType<string[]>) => (value: string | string[]) => {
    let newItems = [...stack.value];
    newItems = typeof value === 'object' ? [...newItems, ...value] : [...newItems, value];

    onFieldChange({ field: stack.field, value: uniq(newItems) });
  };

  const onItemRemoved = (stack: ShareLinkFieldType<string[]>) => (value: string) => {
    onFieldChange({ field: stack.field, value: stack.value.filter((item) => item !== value) });
  };

  const validateInput = useCallback((input: string | string[]): boolean => {
    if (typeof input === 'object') {
      return input.every(validateInput);
    }
    return !!(input.match(EMAIL_GLOBAL_REGEX) || input.match(DOMAIN_REGEX));
  }, []);

  return (
    <>
      <div className='flex flex-col text-bedrock-black'>
        <span className='text-bedrock-p-strong text-bedrock-black'>Manage Whitelist</span>
        <span className='text-bedrock-p-small mt-1'>Enter a domain name or email address to give access</span>
        <MultiSelectInput
          containerClassName='mt-4'
          values={whitelistedEmails.value}
          id='whitelist-input'
          placeholder='Type an email address and press enter'
          onItemAdd={onItemAded(whitelistedEmails)}
          onItemRemove={onItemRemoved(whitelistedEmails)}
          validateInput={validateInput}
          disabled={disabled}
        />
      </div>
      <div className='flex flex-col text-bedrock-black'>
        <span className='text-bedrock-p-strong text-bedrock-black'>Manage Blacklist</span>
        <span className='text-bedrock-p-small mt-1'>Enter a domain name or email address to deny access</span>
        <MultiSelectInput
          containerClassName='mt-4'
          values={blacklistedEmails.value}
          id='blacklist-input'
          placeholder='Type an email address and press enter'
          onItemAdd={onItemAded(blacklistedEmails)}
          onItemRemove={onItemRemoved(blacklistedEmails)}
          validateInput={validateInput}
          disabled={disabled}
        />
      </div>
    </>
  );
};

export const ShareEmailCapture: VFC<ShareEmailCaptureProps> = ({
  emailRequired,
  emailVerificationRequired,
  ndaRequired,
  onFieldChange,
  whitelistedEmails,
  blacklistedEmails,
  disabled = false,
}) => {
  
  const onEmailRequiredToggleChanged = (value: boolean) => {
    onFieldChange({ field: emailRequired.field, value: value });
    if (!value) {
      onFieldChange({ field: emailVerificationRequired.field, value: false });
      if (whitelistedEmails.value.length > 0 || blacklistedEmails.value.length > 0) {
        onFieldChange({ field: whitelistedEmails.field, value: [] });
        onFieldChange({ field: blacklistedEmails.field, value: [] });
      }
    }
  };

  const onEmailVerificationToggleChanged = (value: boolean) => {
    onFieldChange({ field: emailVerificationRequired.field, value: value });
  };

  const maybeRenderToggleWithTooltip = (toggle: JSX.Element, tooltip: string) => {
    if (tooltip) {
      return (
        <TooltipComponent
          tooltipContent={tooltip}
          placement='top'
          theme='journey'
          styling='tooltip'
          containerClassName='flex-1'
        >
          {toggle}
        </TooltipComponent>
      );
    }

    return toggle;
  };

  return (
    <div className='flex flex-col space-y-6 py-6'>
      {maybeRenderToggleWithTooltip(
        <div className='flex flex-col space-y-sm'>
          <ToggleComponent
            groupClassName='flex-1'
            checked={emailRequired.value}
            disabled={ndaRequired.value || disabled}
            onChange={onEmailRequiredToggleChanged}
            label='Require email to view'
            labelType='strong'
          />
          <div className='text-bedrock-p-small text-bedrock-black'>
            Control whitelists, blacklists, and whether users need to submit an email address to view
          </div>
        </div>,
        ndaRequired.value ? 'This is required when the NDA is turned on' : ''
      )}
      {emailRequired.value && (
        <>
          {maybeRenderToggleWithTooltip(
            <div className='flex flex-col space-y-sm'>
              <ToggleComponent
                groupClassName='flex-1'
                checked={emailVerificationRequired.value}
                onChange={onEmailVerificationToggleChanged}
                disabled={ndaRequired.value || disabled}
                label='Require emails to be verified'
                labelType='strong'
              />
              <div className='text-bedrock-p-small text-bedrock-black'>
                Viewers must verify their email by entering an access code sent to their email
              </div>
            </div>,
            ndaRequired.value ? 'This is required when the NDA is turned on' : ''
          )}
          <ShareWhitelistBlacklist
            onFieldChange={onFieldChange}
            whitelistedEmails={whitelistedEmails}
            blacklistedEmails={blacklistedEmails}
            disabled={disabled}
          />
        </>
      )}
    </div>
  );
};
